const PIXEL_ID = process.env.META_PIXEL_ID

const isBrowser = typeof window !== "undefined"

const isPixelReady = () => isBrowser && PIXEL_ID && typeof window.fbq === "function"

export const trackPageView = () => {
  if (!isPixelReady()) return
  window.fbq("track", "PageView")
}

export const trackEvent = (eventName, params = {}, eventId) => {
  if (!isPixelReady()) return
  if (eventId) window.fbq("track", eventName, params, { eventID: eventId })
  else window.fbq("track", eventName, params)
}

/*
 * ADR-112 — Meta's "manual advanced matching": the visitor's own contact
 * details from the form, given to the pixel for the Lead.
 *
 * Every Lead reached Meta with nothing but the browser id, so Meta could rarely
 * tie it to a person; Events Manager reported low data quality and asked for
 * exactly this. The pixel's automatic matching does not read our forms (tested
 * 2026-11-02 with it switched on). The pixel hashes these values (SHA-256)
 * before they leave the browser.
 *
 * Only the e-mail, phone and name fields are read, by type or by title, so
 * nothing else on a form (personnummer, reg-nummer...) can reach Meta. On the
 * pages with a pixel the name field is titled "Namn" on every form; the phone
 * is a "tel" or "phone_number" field or a text field titled "Telefonnummer".
 */
const EMAIL_TITLE = /^(e-?post|e-?postadress|e-?mail|mejl)$/
const PHONE_TITLE = /^(telefon|telefonnummer|mobil|mobilnummer|tel)$/
const NAME_TITLE = /^(namn|ditt namn|för- och efternamn|name)$/

/** Digits with the country code, as Meta wants them: "070-123 45 67" → "46701234567". */
export function normalizePhone(value) {
  let digits = String(value || "").replace(/\D/g, "")
  if (digits.startsWith("00")) digits = digits.slice(2)
  else if (digits.startsWith("0")) digits = `46${digits.slice(1)}`
  // "+46 (0)8 ...": a Swedish number never keeps its leading 0 after the country code.
  return digits.startsWith("460") ? `46${digits.slice(3)}` : digits
}

/** Meta's match keys from a form's fields, given as { title, fieldType, value }. */
export function matchingDataFrom(fields) {
  const data = {}
  for (const { title, fieldType, value } of fields || []) {
    if (typeof value !== "string" || !value.trim()) continue
    // A field without a type is rendered as a text input.
    const type = String(fieldType || "text").toLowerCase()
    const label = String(title || "").trim().toLowerCase()
    if (type === "email" || (type === "text" && EMAIL_TITLE.test(label))) {
      if (!data.em && value.includes("@")) data.em = value.trim().toLowerCase()
    } else if (type === "tel" || type === "phone_number" || (type === "text" && PHONE_TITLE.test(label))) {
      const phone = normalizePhone(value)
      if (!data.ph && phone.length >= 8) data.ph = phone
    } else if (type === "name" || (type === "text" && NAME_TITLE.test(label))) {
      const words = value.trim().toLowerCase().split(/\s+/)
      if (!data.fn) {
        data.fn = words[0]
        if (words.length > 1) data.ln = words[words.length - 1]
      }
    }
  }
  return data
}

/** Gives the pixel the visitor's details; the events after it carry them. */
export const identifyVisitor = userData => {
  if (!isPixelReady() || !userData || Object.keys(userData).length === 0) return
  window.fbq("init", PIXEL_ID, userData)
}

/** One Lead's id, so a server-side copy of it can later be told apart from a second lead. */
export const newEventId = () => {
  const random =
    isBrowser && window.crypto && typeof window.crypto.randomUUID === "function"
      ? window.crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`
  return `lead-${random}`
}

export const trackCustomEvent = (eventName, params = {}) => {
  if (!isPixelReady()) return
  window.fbq("trackCustom", eventName, params)
}

export const trackLead = (params = {}, eventId) => trackEvent("Lead", params, eventId)

export const trackContact = (params = {}) => trackEvent("Contact", params)

export const trackCompleteRegistration = (params = {}) =>
  trackEvent("CompleteRegistration", params)

const isPrimaryClick = (e) =>
  e.button === 0 && !e.ctrlKey && !e.metaKey && !e.shiftKey && !e.altKey

let linkClickTrackingInitialized = false

export const setupLinkClickTracking = () => {
  if (!isBrowser) return
  if (linkClickTrackingInitialized) return
  linkClickTrackingInitialized = true

  document.addEventListener("click", (e) => {
    if (!isPrimaryClick(e)) return

    const link = e.target.closest("a[href]")
    if (!link) return

    const href = link.getAttribute("href") || ""

    // ADR-112 — which kind of contact, not the address or number itself: an
    // e-mail address or phone number in an event parameter is personal data
    // to Meta, whoever's it is.
    if (href.startsWith("mailto:")) {
      trackContact({ content_name: "email" })
      return
    }

    if (href.startsWith("tel:")) {
      trackContact({ content_name: "phone" })
    }
  })
}
